import { Link } from 'react-router-dom'
import { Inbox, Activity } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { apiJson } from '@/api/client'
import { formatTimeAgo } from '@/lib/format'
import type { AlertFilters } from '../../types'

interface Props {
  filters: AlertFilters
  onClearFilters: () => void
  onResetToMyQueue: () => void
}

/**
 * Adaptive empty state — names the most-restrictive active filter so the
 * analyst knows what to remove. Falls back to a "nothing in the queue"
 * variant with the last-alert timestamp + a pipeline-status link, so the
 * analyst can disambiguate "caught up" from "ingestion broken".
 */
export function EmptyQueue({ filters, onClearFilters, onResetToMyQueue }: Props) {
  const restrictive = mostRestrictiveLabel(filters)

  if (!restrictive) {
    return <NothingInQueue />
  }

  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
      <div className="rounded-full bg-muted p-3">
        <Inbox size={20} className="text-muted-fg" />
      </div>
      <p className="text-sm font-medium">No alerts match these filters.</p>
      <p className="max-w-md text-xs text-muted-fg">
        Try removing <span className="font-medium text-fg">{restrictive}</span>, or reset to{' '}
        <button
          type="button"
          onClick={onResetToMyQueue}
          className="text-primary underline-offset-2 hover:underline"
        >
          My Queue
        </button>
        .
      </p>
      <button
        type="button"
        onClick={onClearFilters}
        className="rounded border border-border px-3 py-1 text-xs hover:bg-muted"
      >
        Clear filters
      </button>
    </div>
  )
}

function NothingInQueue() {
  // The "caught up vs broken pipeline" disambiguator. When the analyst sees
  // an empty queue, they need to know whether the last alert was 12 minutes
  // ago (everything's fine) or 4 hours ago (someone should look at ingest).
  const { data } = useQuery({
    queryKey: ['alerts', 'last-event-at'],
    queryFn: () => apiJson<{ last_alert_at: string | null }>('/alerts/last-event-at'),
    staleTime: 30_000,
  })

  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
      <div className="rounded-full bg-muted p-3">
        <Inbox size={20} className="text-muted-fg" />
      </div>
      <p className="text-sm font-medium">Nothing in the queue.</p>
      <p className="max-w-md text-xs text-muted-fg">
        Either you're caught up — or alerts aren't flowing.
        {data?.last_alert_at && (
          <>
            {' '}The last alert came in{' '}
            <span className="font-medium text-fg">{formatTimeAgo(data.last_alert_at)}</span>.
          </>
        )}
      </p>
      <Link
        to="/workflow"
        className="inline-flex items-center gap-1.5 rounded border border-border px-3 py-1 text-xs hover:bg-muted"
      >
        <Activity size={12} />
        View pipeline status
      </Link>
    </div>
  )
}

function mostRestrictiveLabel(filters: AlertFilters): string | null {
  if (filters.sla_breached) return 'SLA: At-risk'
  if (filters.sla_at_risk) return 'SLA: Near-breach'
  if (filters.priority?.length) return `Priority: ${filters.priority.join(', ')}`
  if (filters.status?.length) return `Status: ${filters.status.join(', ')}`
  if (filters.assigned_to === 'unassigned') return 'Assigned: Unassigned'
  if (Array.isArray(filters.assigned_to) && filters.assigned_to.length > 0)
    return 'Assigned to a specific user'
  if (filters.queue_id?.length) return `Queue: ${filters.queue_id.join(', ')}`
  if (filters.channel?.length) return `Channel: ${filters.channel.join(', ')}`
  if (filters.q) return `Search: "${filters.q}"`
  if (filters.amount_min_minor || filters.amount_max_minor) return 'Amount range'
  return null
}
